import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import axios from "axios";
import StarsIcon from "@mui/icons-material/Stars";
import RedeemIcon from "@mui/icons-material/Redeem";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BoltIcon from "@mui/icons-material/Bolt";
import SecurityIcon from "@mui/icons-material/Security";
import DevicesIcon from "@mui/icons-material/Devices";
import DownloadIcon from "@mui/icons-material/Download";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WarningIcon from "@mui/icons-material/Warning";
import LockIcon from "@mui/icons-material/Lock";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { API_URL, popupTimer } from "../../consts";
import "../Profile/Profile.css";

const PointsMarket = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.user);
  const [balance, setBalance] = React.useState(userData?.points || 0);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [redeemingId, setRedeemingId] = React.useState(null);
  const [redeemedCounts, setRedeemedCounts] = React.useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [itemToRedeem, setItemToRedeem] = React.useState(null);

  const fireToast = (text, type = "success") => {
    dispatch({ type: "SET_POPUP", payload: { text, type } });
    setTimeout(
      () => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }),
      popupTimer
    );
  };

  const mapIcon = (iconName) => {
    switch (iconName) {
      case "bolt":
        return <BoltIcon />;
      case "stars":
        return <StarsIcon />;
      case "devices":
        return <DevicesIcon />;
      case "download":
        return <DownloadIcon />;
      case "security":
        return <SecurityIcon />;
      case "play":
        return <PlayCircleOutlineIcon />;
      default:
        return <RedeemIcon />;
    }
  };

  const loadRewards = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/points/rewards`);
      const rewards = res?.data?.items || [];
      setItems(
        rewards.map((item) => ({
          ...item,
          ...(item.id === "pro-title"
            ? {
                title: "Expert Badge",
                description:
                  "An expert badge displayed next to your username wherever it appears.",
              }
            : {}),
          icon: mapIcon(item.icon),
          accent: item.accent || "linear-gradient(135deg, #6366f1, #8b5cf6)",
        }))
      );
      setBalance(res?.data?.balance ?? userData?.points ?? 0);
      const redemptionMap = {};
      (res?.data?.redemptions || []).forEach((r) => {
        const rewardId = r?.action_details?.reward_id || r.reward_id;
        if (rewardId) {
          redemptionMap[rewardId] = (redemptionMap[rewardId] || 0) + (r?.action_details?.quantity || 1);
        }
      });
      setRedeemedCounts(redemptionMap);
    } catch (err) {
      console.error("Failed to load rewards", err);
      fireToast(err?.response?.data?.error || "Failed to load rewards", "error");
    } finally {
      setLoading(false);
    }
  }, [userData]);

  React.useEffect(() => {
    loadRewards();
  }, [loadRewards]);

  const handleRedeemClick = (item) => {
    if (balance < item.cost) {
      fireToast("Not enough points for this reward", "error");
      return;
    }
    setItemToRedeem(item);
    setConfirmDialogOpen(true);
  };

  const handleConfirmRedeem = async () => {
    if (!itemToRedeem) return;
    
    const quantity = 1;
    try {
      setConfirmDialogOpen(false);
      setRedeemingId(itemToRedeem.id);
      const res = await axios.post(`${API_URL}/points/redeem`, {
        reward_id: itemToRedeem.id,
        quantity,
      });
      setBalance(res?.data?.balance ?? balance - itemToRedeem.cost * quantity);
      setRedeemedCounts((prev) => ({
        ...prev,
        [itemToRedeem.id]: (prev[itemToRedeem.id] || 0) + quantity,
      }));
      
      // Check if this is a title badge reward
      const isTitleReward = itemToRedeem.id === "pro-title" || itemToRedeem.id === "intermediate-title";
      if (isTitleReward) {
        const titleName = itemToRedeem.title.replace(" Badge", "");
        fireToast(
          `Success! The "${titleName}" badge has been added to your username. It will be displayed next to your name everywhere and is valid for one year.`,
          "success"
        );
      } else {
        fireToast(`Redeemed "${itemToRedeem.title}"`, "success");
      }
      setItemToRedeem(null);
    } catch (err) {
      console.error("Redeem failed", err);
      fireToast(err?.response?.data?.error || "Redeem failed", "error");
    } finally {
      setRedeemingId(null);
    }
  };

  const handleCancelRedeem = () => {
    setConfirmDialogOpen(false);
    setItemToRedeem(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{
          background: "linear-gradient(135deg, #0ea5e9, #7c3aed)",
          color: "#fff",
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          boxShadow: "0 20px 45px rgba(99,102,241,0.25)",
        }}
      >
        <Stack spacing={1}>
          <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1 }}>
            Rewards
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Points Market
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 620, opacity: 0.9 }}>
            Redeem your points for perks. Your balance updates in real-time after each redemption.
          </Typography>
        </Stack>
        <Card
          elevation={0}
          sx={{
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff",
            minWidth: 240,
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)" }}>
                <StarsIcon />
              </Avatar>
              <Box>
                <Typography variant="overline" sx={{ opacity: 0.7 }}>
                  Available Points
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {balance} pts
                </Typography>
              </Box>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min((balance / 1500) * 100, 100)}
              sx={{
                mt: 2,
                bgcolor: "rgba(255,255,255,0.18)",
                "& .MuiLinearProgress-bar": {
                  background: "#fbbf24",
                },
              }}
            />
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Points reflect your live balance.
            </Typography>
          </CardContent>
        </Card>
      </Stack>

   

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {items.map((item) => {
          const disabled = balance < item.cost || Boolean(redeemingId);
          const redeemedCount = redeemedCounts[item.id] || 0;
          return (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  height: "100%",
                  border: "1px solid rgba(226,232,240,0.8)",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
                  transition: "all 0.25s ease",
                  position: "relative",
                  "&:hover": { boxShadow: "0 18px 36px rgba(15,23,42,0.08)", transform: "translateY(-4px)" },
                }}
              >
                {disabled && balance < item.cost && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      zIndex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "rgba(0, 0, 0, 0.6)",
                      color: "#fff",
                    }}
                  >
                    <LockIcon sx={{ fontSize: 18 }} />
                  </Box>
                )}
                <CardContent>
                  {(item.id === "pro-title" || item.id === "intermediate-title") ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mb: 1.5,
                        minHeight: 52,
                      }}
                    >
                      <Box className="medal-container">
                        <Box className={`medal ${item.id === "pro-title" ? "pro" : "intermediate"}`}>
                          <span className="medal-text">
                            {item.id === "pro-title" ? "EXPERT" : "INTERMEDIATE"}
                          </span>
                          <Box className="shimmer"></Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2,
                          color: "#fff",
                          background: item.accent,
                        }}
                      >
                        {item.icon}
                      </Box>
                    </Box>
                  )}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    {item.badge && <Chip size="small" label={item.badge} color="primary" />}
                    {redeemedCount > 0 && (
                      <Chip size="small" label={`Redeemed x${redeemedCount}`} color="success" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {item.description}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <StarsIcon sx={{ color: "rgb(0,202,153)" }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.cost} pts
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Tooltip title={disabled ? "Not enough points" : "Redeem"}>
                      <span style={{ width: "100%" }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={redeemedCount > 0 ? <CheckCircleIcon /> : <ShoppingCartIcon />}
                          onClick={() => handleRedeemClick(item)}
                          disabled={disabled}
                          sx={{
                            py: 1.25,
                            borderRadius: 2,
                            background: redeemedCount > 0
                              ? "linear-gradient(135deg, #22c55e, #16a34a)"
                              : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                            color: redeemedCount > 0 ? "#fff" : "#1a1a1a",
                            "&:hover": {
                              background: redeemedCount > 0
                                ? "linear-gradient(135deg, #22c55e, #16a34a)"
                                : "linear-gradient(135deg, #5850ec, #7c3aed)",
                            },
                          }}
                        >
                          {redeemingId === item.id ? "Processing..." : redeemedCount > 0 ? "Redeemed" : "Use points"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {!loading && items.length === 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2">No rewards available right now.</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Card
        elevation={0}
        sx={{
          mt: 3,
          borderRadius: 3,
          border: "1px solid rgba(226,232,240,0.8)",
          boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
          background: "linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.05))",
        }}
      >
        <CardContent sx={{ py: 3 }}>
          <Stack direction="row" justifyContent="center" alignItems="center">
            <Button
              variant="contained"
              startIcon={<StarsIcon />}
              onClick={() => navigate("/earn-points")}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                boxShadow: "0 8px 20px rgba(99,102,241,0.3)",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                "&:hover": {
                  background: "linear-gradient(135deg, #5850ec, #7c3aed)",
                  boxShadow: "0 12px 28px rgba(99,102,241,0.4)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              How to Get Points
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelRedeem}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningIcon color="warning" />
            <Typography variant="h6">Confirm Redemption</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {itemToRedeem && (
              <>
                <Typography variant="body1">
                  You are about to redeem <strong>{itemToRedeem.title}</strong>.
                </Typography>
                <Divider />
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Current balance:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {balance} pts
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Points to deduct:
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                      -{itemToRedeem.cost} pts
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Remaining balance:
                    </Typography>
                    <Typography variant="body1" color="primary" sx={{ fontWeight: 700 }}>
                      {balance - itemToRedeem.cost} pts
                    </Typography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelRedeem} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRedeem}
            variant="contained"
            color="primary"
            sx={{
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              "&:hover": {
                background: "linear-gradient(135deg, #5850ec, #7c3aed)",
              },
            }}
          >
            Confirm Redemption
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PointsMarket;


